import Tooltip from '@mui/material/Tooltip'
import { useEffect, useMemo, useRef, useState } from 'react'
import {
  BiPause,
  BiPlay,
  BiSkipNext,
  BiSkipPrevious,
  BiVolumeFull,
  BiX,
} from 'react-icons/bi'
import styled, { css } from 'styled-components'

const defaultProps = {}
const propTypes = {}

const WORDS_PER_MIN = 150

const Wrapper = styled.div`
  display: flex;
  align-items: center;
  height: fit-content;
  width: fit-content;
  max-width: 100%;
  margin: -10px 0 20px;
  color: ${({ theme }) => theme.colors.grey};
  ${({ active }) =>
    active &&
    css`
      position: sticky;
      top: 80px;
      z-index: 5;
      padding: 8px 14px;
      border: solid 1px #e0e0e0;
      border-radius: 12px;
      background: #fff;
      box-shadow: 0 2px 10px rgba(0, 0, 0, 0.08);
    `}
`

const ListenTrigger = styled.div`
  display: flex;
  align-items: center;
  cursor: pointer;
  white-space: nowrap;
  ${({ theme }) => theme.font.body2}
  &:hover {
    color: ${({ theme }) => theme.colors.primary};
  }
`

const TriggerIcon = styled(BiVolumeFull)`
  height: 28px;
  width: 28px;
  margin-right: 4px;
`

const Player = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`

const IconBtn = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  border: none;
  background: none;
  cursor: pointer;
  color: ${({ theme }) => theme.colors.grey};
  &:hover {
    color: ${({ theme }) => theme.colors.primary};
  }
  ${({ primary }) =>
    primary &&
    css`
      color: ${({ theme }) => theme.colors.primary};
    `}
  svg {
    height: ${({ big }) => (big ? 30 : 24)}px;
    width: ${({ big }) => (big ? 30 : 24)}px;
  }
`

const Seek = styled.input.attrs({ type: 'range' })`
  -webkit-appearance: none;
  appearance: none;
  width: 90px;
  height: 4px;
  border-radius: 2px;
  cursor: pointer;
  background: ${({ theme, value, max }) => {
    const pct = max > 0 ? (value / max) * 100 : 0
    return `linear-gradient(to right, ${theme.colors.primary} ${pct}%, #e0e0e0 ${pct}%)`
  }};
  &::-webkit-slider-thumb {
    -webkit-appearance: none;
    appearance: none;
    height: 12px;
    width: 12px;
    border-radius: 50%;
    background: ${({ theme }) => theme.colors.primary};
  }
  &::-moz-range-thumb {
    height: 12px;
    width: 12px;
    border: none;
    border-radius: 50%;
    background: ${({ theme }) => theme.colors.primary};
  }
`

const Time = styled.span`
  ${({ theme }) => theme.font.body2}
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
`

const SpeedBtn = styled.button`
  ${({ theme }) => theme.font.body2}
  border: solid 1px ${({ theme }) => theme.colors.grey};
  border-radius: 12px;
  background: none;
  padding: 1px 8px;
  cursor: pointer;
  color: ${({ theme }) => theme.colors.grey};
  &:hover {
    color: ${({ theme }) => theme.colors.primary};
    border-color: ${({ theme }) => theme.colors.primary};
  }
`

// HTML string -> plain readable text
const htmlToText = html => {
  if (!html || typeof document === 'undefined') {
    return ''
  }
  const el = document.createElement('div')
  el.innerHTML = html
  return (el.textContent || el.innerText || '').replace(/\s+/g, ' ').trim()
}

// Chrome cuts long utterances (~15s). Split into sentence chunks.
const chunkText = (text, max = 200) => {
  const sentences = text.match(/[^.!?]+[.!?]+|\s*[^.!?]+$/g) || [text]
  const chunks = []
  let current = ''
  sentences.forEach(s => {
    if ((current + s).length > max) {
      if (current) {
        chunks.push(current.trim())
      }
      current = s
    } else {
      current += s
    }
  })
  if (current.trim()) {
    chunks.push(current.trim())
  }
  return chunks
}

const fmtTime = secs => {
  if (!isFinite(secs) || secs < 0) {
    secs = 0
  }
  const m = Math.floor(secs / 60)
  const s = Math.floor(secs % 60)
  return `${m}:${s.toString().padStart(2, '0')}`
}

const VOICE_NAME = 'Daniel'
const LANG = 'en-GB'

// Daniel -> any en-GB -> any English. Never null when an English voice exists,
// so the engine can't fall back to the OS locale (e.g. pt-BR).
const resolveVoice = () => {
  const voices = window.speechSynthesis.getVoices()
  if (!voices.length) {
    return null
  }
  const lower = VOICE_NAME.toLowerCase()
  return (
    voices.find(v => v.name === VOICE_NAME) ||
    voices.find(v => v.name.toLowerCase().includes(lower)) ||
    voices.find(v => v.lang === LANG) ||
    voices.find(v => v.lang && v.lang.toLowerCase().startsWith('en')) ||
    null
  )
}
const SPEEDS = [1, 1.5, 2, 0.75]
const STATE = { IDLE: 'idle', PLAYING: 'playing', PAUSED: 'paused' }

export const ListenButton = props => {
  const { title, content, style } = props
  const [state, setState] = useState(STATE.IDLE)
  const [supported, setSupported] = useState(true)
  const [progress, setProgress] = useState(0) // 0..1 over total chars
  const [rate, setRate] = useState(1)
  const playIdRef = useRef(0)
  const indexRef = useRef(0)
  const rateRef = useRef(1)
  const voiceRef = useRef(null)

  useEffect(() => {
    if (typeof window === 'undefined' || !('speechSynthesis' in window)) {
      setSupported(false)
      return undefined
    }
    const pickVoice = () => {
      voiceRef.current = resolveVoice()
    }
    pickVoice()
    window.speechSynthesis.addEventListener('voiceschanged', pickVoice)
    return () =>
      window.speechSynthesis.removeEventListener('voiceschanged', pickVoice)
  }, [])

  const text = useMemo(() => {
    const body = htmlToText(content)
    return [title, body].filter(Boolean).join('. ')
  }, [title, content])

  // chunks + cumulative char offsets for smooth progress / seeking
  const { chunks, offsets, totalChars, totalWords } = useMemo(() => {
    const ch = chunkText(text)
    const offs = []
    let acc = 0
    ch.forEach(c => {
      offs.push(acc)
      acc += c.length
    })
    return {
      chunks: ch,
      offsets: offs,
      totalChars: acc || 1,
      totalWords: text ? text.split(/\s+/).filter(Boolean).length : 0,
    }
  }, [text])

  const totalSecs = useMemo(
    () => (totalWords / WORDS_PER_MIN / rate) * 60,
    [totalWords, rate]
  )

  const stop = () => {
    playIdRef.current += 1
    if (typeof window !== 'undefined' && 'speechSynthesis' in window) {
      window.speechSynthesis.cancel()
    }
  }

  // Stop on unmount or content change
  useEffect(() => stop, [text])

  const speakFrom = (index, playId) => {
    const synth = window.speechSynthesis
    if (playId !== playIdRef.current) {
      return
    }
    if (index >= chunks.length) {
      indexRef.current = 0
      setProgress(1)
      setState(STATE.IDLE)
      return
    }
    indexRef.current = index
    const utterance = new window.SpeechSynthesisUtterance(chunks[index])
    utterance.rate = rateRef.current
    utterance.pitch = 0.9
    utterance.lang = LANG
    if (!voiceRef.current) {
      voiceRef.current = resolveVoice()
    }
    if (voiceRef.current) {
      utterance.voice = voiceRef.current
    }
    utterance.onboundary = e => {
      if (playId === playIdRef.current) {
        setProgress((offsets[index] + (e.charIndex || 0)) / totalChars)
      }
    }
    utterance.onend = () => speakFrom(index + 1, playId)
    utterance.onerror = () => {
      if (playId === playIdRef.current) {
        setState(STATE.IDLE)
      }
    }
    synth.speak(utterance)
  }

  const playFrom = index => {
    stop()
    const playId = playIdRef.current
    setState(STATE.PLAYING)
    setProgress(offsets[index] / totalChars)
    speakFrom(index, playId)
  }

  const toggle = () => {
    if (!supported || !text) {
      return
    }
    const synth = window.speechSynthesis
    if (state === STATE.IDLE) {
      playFrom(0)
    } else if (state === STATE.PLAYING) {
      synth.pause()
      setState(STATE.PAUSED)
    } else if (state === STATE.PAUSED) {
      synth.resume()
      setState(STATE.PLAYING)
    }
  }

  // Map a 0..1 fraction to the chunk index whose range contains it
  const indexForFraction = fraction => {
    const targetChar = fraction * totalChars
    let idx = 0
    for (let i = 0; i < offsets.length; i += 1) {
      if (offsets[i] <= targetChar) {
        idx = i
      } else {
        break
      }
    }
    return idx
  }

  const seek = fraction => playFrom(indexForFraction(fraction))

  const skip = delta => {
    const next = Math.min(
      Math.max(indexRef.current + delta, 0),
      chunks.length - 1
    )
    playFrom(next)
  }

  const close = () => {
    stop()
    indexRef.current = 0
    setProgress(0)
    setState(STATE.IDLE)
  }

  const cycleSpeed = () => {
    const next = SPEEDS[(SPEEDS.indexOf(rate) + 1) % SPEEDS.length]
    setRate(next)
    rateRef.current = next
    // rate can't change mid-utterance -> restart current chunk if playing
    if (state !== STATE.IDLE) {
      playFrom(indexRef.current)
    }
  }

  if (!supported) {
    return null
  }

  const estMins = Math.max(1, Math.round(totalSecs / 60))

  if (state === STATE.IDLE) {
    return (
      <Tooltip title="Listen to article" placement="top">
        <Wrapper style={style}>
          <ListenTrigger onClick={toggle}>
            <TriggerIcon />
            Listen{totalWords ? ` ~${estMins} min` : ''}
          </ListenTrigger>
        </Wrapper>
      </Tooltip>
    )
  }

  const maxSteps = 1000
  const elapsed = totalSecs * progress

  return (
    <Wrapper active style={style}>
      <Player>
        <Tooltip title="Previous" placement="top">
          <IconBtn onClick={() => skip(-1)} aria-label="Previous">
            <BiSkipPrevious />
          </IconBtn>
        </Tooltip>
        <Tooltip
          title={state === STATE.PLAYING ? 'Pause' : 'Resume'}
          placement="top"
        >
          <IconBtn big primary onClick={toggle} aria-label="Play or pause">
            {state === STATE.PLAYING ? <BiPause /> : <BiPlay />}
          </IconBtn>
        </Tooltip>
        <Tooltip title="Next" placement="top">
          <IconBtn onClick={() => skip(1)} aria-label="Next">
            <BiSkipNext />
          </IconBtn>
        </Tooltip>
        <Seek
          min={0}
          max={maxSteps}
          value={Math.round(progress * maxSteps)}
          onChange={e => seek(Number(e.target.value) / maxSteps)}
          aria-label="Seek"
        />
        <Time>
          {fmtTime(elapsed)} / {fmtTime(totalSecs)}
        </Time>
        <Tooltip title="Playback speed" placement="top">
          <SpeedBtn onClick={cycleSpeed} aria-label="Playback speed">
            {rate}x
          </SpeedBtn>
        </Tooltip>
        <Tooltip title="Close player" placement="top">
          <IconBtn onClick={close} aria-label="Close player">
            <BiX />
          </IconBtn>
        </Tooltip>
      </Player>
    </Wrapper>
  )
}

ListenButton.defaultProps = defaultProps
ListenButton.propTypes = propTypes

export default ListenButton
